import type { ReactNode } from "react";
import { StyleSheet, View, type ViewProps } from "react-native";

import type { UiTheme } from "../theme";

// <MeterRow> (MX-5 slice 1) — the GEOMETRY of a logger meter row, and nothing
// else: a flexible leading area, a gap, and a fixed-width column pinned to the
// right. Extracted out of <EffortMeter>, where the measurements used to live
// tangled with the content, because MX-5 puts a SECOND thing on that same grid
// — the hold/cardio clock — and the two must line up to the pixel.
//
// ⚠️ THE RULE, rewritten 2026-07-22 (DS-27): **the row always fits.** It used to
// carry a hard floor on the flexible area (5 × 44, so the effort band's segments
// could never fall under the touch floor) beside a column that doesn't shrink.
// Neither side yielding means the row cannot absorb a narrow screen: it
// OVERFLOWED its card instead of degrading, and overflow is not a cosmetic
// problem — the content gets clipped. Measured on device at 320dp: `0:3` for
// `0:30`, `HEL..` for `HELD`, and the last segment printing `1` where it means
// `10`, i.e. failure reading as one rep in reserve. A target 9pt narrower is
// still usable; a number that lies is not.
//
// So the flexible area now shrinks, and what is defended instead is HEIGHT: 44
// tall, always. That is the half of a tap target the thumb actually needs on a
// row of side-by-side segments — you miss horizontally between neighbours (and
// land on an adjacent effort, correctable with another tap), not vertically.
//
// Who is actually narrow, since this was got wrong once (Antenore 2026-07-22):
// NOT old hardware — no Android in circulation is under 360dp. It is Android's
// **Display size** setting (Settings → Display → Display size and text; iOS has
// Display Zoom): the slider that enlarges the whole interface and, in exchange,
// hands the app a smaller canvas. It reaches ~1.3× on stock, so the narrowest
// case worth serving is 360dp ÷ 1.3 ≈ **280dp**, on an ordinary modern phone
// belonging to someone who needs the interface bigger. (Font scale is a
// different setting: it changes text size, not the window's dp width.)

/** Touch floor of the row — both areas are at least this tall. This one is a
 *  floor, not a preference: it is the axis a thumb can't miss on. */
export const METER_ROW_MIN_HEIGHT = 44;

/** The fixed right-hand column: the reps set's Fail box, the timed set's start
 *  chip, the held-time readout. ONE width for every screen and every state
 *  (Antenore 2026-07-22: "il bottone potrebbe essere ridotto globalmente a una
 *  colonna"). It was 68 for Fail and 76 for the timer slot — about two ordinary
 *  36pt icon buttons — and that width is what pushed the row out of the card.
 *  48 still clears the touch floor, still holds "Fail" and `0:30`/`HELD` at
 *  130% text scale, and needs no breakpoint rule: the previous narrow-screen
 *  exception is gone, which is one fewer number that can drift. */
export const METER_ROW_COLUMN = 48;

export type MeterRowProps = {
  theme: UiTheme;
  /** The flexible area: the effort band, or the running clock. */
  children: ReactNode;
  /** The fixed right-hand column. Omitted → the leading area takes the whole
   *  row, which is what a running timer does (the band is the only control
   *  that matters then, so it gets every point of width). */
  trailing?: ReactNode;
  /** Row-level a11y role — the effort band's row IS the radiogroup, so the
   *  role belongs on the container this primitive renders. */
  accessibilityRole?: ViewProps["accessibilityRole"];
};

export function MeterRow({
  theme,
  children,
  trailing,
  accessibilityRole,
}: MeterRowProps) {
  const styles = makeStyles(theme);
  return (
    <View style={styles.row} accessibilityRole={accessibilityRole}>
      <View style={styles.leading}>{children}</View>
      {trailing != null ? (
        <View style={styles.column}>{trailing}</View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    // stretch, not center: the column matches the leading area's height, so a
    // taller band (font scale) grows the Fail box with it instead of leaving
    // it floating at 44.
    row: { flexDirection: "row", gap: s.s3, alignItems: "stretch" },
    // ⚠️ BOTH areas are ROW containers, and that is load-bearing, not styling.
    // Their contents (the band, the Fail box, the action slot) are written
    // `flex: 1` meaning "take the width I'm given" — which is what it meant
    // when they were direct children of the row above. Leave these as the
    // default column and that same `flex: 1` silently starts meaning "take all
    // the HEIGHT", and the band grows to eat the screen (regression shipped in
    // v4.3.1-dev, cardio panel). Height comes from minHeight + the row's
    // stretch, never from a child growing.
    //
    // No minWidth: see the rule at the top. The area yields width so the row
    // can never overflow the card; the segments inside get narrower and stay
    // 44 tall.
    leading: {
      flex: 1,
      flexDirection: "row",
      minWidth: 0,
      minHeight: METER_ROW_MIN_HEIGHT,
    },
    // flexShrink 0: an unbounded column grew to fit its content and stole the
    // band's width (a11y audit AP-0b — the Fail sub-label did exactly that).
    column: {
      width: METER_ROW_COLUMN,
      flexShrink: 0,
      flexDirection: "row",
      minHeight: METER_ROW_MIN_HEIGHT,
    },
  });
}
